/**
 * Terug-fallback per route-patroon (spec navigatie, 404-vangnet).
 *
 * De gedeelde `BackButton` (@streep/ui) valt zonder in-app history terug op de
 * parent-route: het pad minus het laatste segment. Voor routes waarvan dat
 * parent-segment geen pagina heeft is dat een 404 (`/scanner/r/<token>` ->
 * `/scanner/r`). Een app geeft zulke routes een expliciete bestemming via een
 * tabel van `[routePatroon, href]`.
 *
 * Een patroon is een Next-route-pad: letterlijke segmenten plus `[param]`-
 * segmenten die precies één willekeurig segment matchen. De match is
 * segment-exact (zelfde aantal segmenten), zodat een entry nooit een diepere
 * route met een wél bestaande parent kaapt: `/verlof/campagne/[id]` matcht
 * `/verlof/campagne/7`, maar niet `/verlof/campagne/7/resultaat` (die valt
 * gewoon op zijn parent terug). Catch-all-segmenten (`[...x]`) worden niet
 * ondersteund; schrijf voor zo'n route het patroon op de exacte diepte.
 *
 * Pure helper (geen DOM/Next) in @streep/utils: de deploy-gate test 'm, en de
 * drift-guard in de api-suite gebruikt dezelfde matcher als de portal-Topbar.
 */

export type BackFallbackTabel = ReadonlyArray<readonly [routePatroon: string, href: string]>

function segmenten(pad: string): string[] {
  return pad.split('?')[0].split('#')[0].split('/').filter(Boolean)
}

/** `[id]`, `[token]`, ... - een dynamisch route-segment. */
export function isParamSegment(segment: string): boolean {
  return segment.startsWith('[') && segment.endsWith(']')
}

/**
 * Matcht een pathname (of een ander route-patroon: `[x]` telt dan als
 * willekeurig segment) op een route-patroon. Query/hash worden genegeerd.
 */
export function matchtRoutePatroon(pathname: string, patroon: string): boolean {
  const pad = segmenten(pathname)
  const pat = segmenten(patroon)
  if (pad.length !== pat.length) return false
  return pat.every((segment, i) => isParamSegment(segment) || segment === pad[i])
}

/**
 * De fallback-bestemming voor `pathname`, of `undefined` als geen patroon past
 * (de caller valt dan terug op de parent-route). Eerste passende entry wint;
 * door de segment-exacte match kunnen entries elkaar alleen overlappen als ze
 * hetzelfde patroon beschrijven.
 */
export function matchBackFallback(pathname: string, tabel: BackFallbackTabel): string | undefined {
  for (const [patroon, href] of tabel) {
    if (matchtRoutePatroon(pathname, patroon)) return href
  }
  return undefined
}
